<template>
  <div>
    <esg-base-filter v-if="filter"
        :metadata="fields"
                     size="sm"
                     class="mb-2"
                     @update:filters="onFilterUpdated"/>
    <esg-base-table :size="size"
                    :items="tableItems"
                    :fields="tableFields"
                    :max-height="maxHeight"
                    :selectable="selectable"
                    :striped="striped"
                    @sortSwitched="onSortSwitched"
                    @rowClicked="onRowClicked">
      <template #thead-top>
        <slot name="thead-top"></slot>
      </template>
      <template v-for="tableField in tableFields"
                v-slot:[slotname(tableField)]="{ value, item, items, field, row, col }">
        <slot :name="slotname(tableField)"
              :value="value"
              :item="item"
              :items="items"
              :field="field"
              :row="row"
              :col="col"></slot>
      </template>
      <template #footer>
        <slot name="footer"></slot>
      </template>
    </esg-base-table>
  </div>
</template>

<script>
import EsgBaseTable from "@/components/esg/base/EsgBaseTable.vue";
import EsgBaseFilter from "@/components/esg/base/EsgBaseFilter.vue";

export default {
  components: { EsgBaseFilter, EsgBaseTable },
  props: {
    filter: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md'
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: 'auto'
    },
    items: {
      type: Array,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    striped: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    fields: {
      deep: true,
      immediate: true,
      handler(fields) {
        this.initialize(fields);
      }
    },
    items: {
      deep: true,
      immediate: true,
      handler(items) {
        this.tableItems = structuredClone(items);
      }
    }
  },
  data() {
    return {
      loading: false,
      tableItems: [],
      tableFields: [],
      formatters: {
        Year: this.formatYear,
        Date: this.formatDatetime,
        Code: this.formatCode,
        String: this.formatString,
        Number: this.formatNumeric,
        Reference: this.formatString,
      }
    }
  },
  created() {
    this.initialize(this.fields);
  },
  methods: {
    async initialize(fields) {
      if (this.fields.length === 0) return;

      try {
        this.setLoading(true);

        // Meta information for columns
        let tableFields = fields.filter(field => field.list);

        // Replace dot(.) in column names with hyphen(_) and use as a key for the column
        tableFields.forEach(field => field.key = field.name.replace('.', '_'));

        // Reorder fields
        tableFields.sort((a, b) => a.order - b.order);

        // Process sorting info
        this.processSorting(tableFields);

        // Process common codes info
        await Promise.all(this.processCommonCodes(tableFields));

        // Set formatter
        tableFields.forEach(field => field.formatter = field.formatter || this.formatters[field.type || 'String'](field));

        this.tableFields = tableFields;
      } catch (error) {
        this.setLoading(false);
        await this.showError(error);
      } finally {
        this.setLoading(false);
      }
    },
    processSorting(fields) {
      fields.forEach(field => field.sortable && (field.sortOrder = null));
    },
    processCommonCodes(fields) {
      return fields.map(async field => {
        if (field.type === 'Code') {
          // Construct filters for tenant ID
          let filters = [{
            name: 'esg_base_common_code.tenant_id',
            operation: 'EQUAL',
            value: this.tenantId()
          }];

          // and categories
          field.categories.forEach((cat, index) => {
            filters.push({
              name: `esg_base_common_code.category_${index + 1}`,
              operation: 'EQUAL',
              value: cat
            })
          });

          // Fetch code info
          let response = await axios.get(`/esg/lca/tables/esg_base_common_code`, {
            headers: { 'Authorization': accessToken() },
            params: { filters }
          });

          // Create options
          let options = []
          let codes = response.data.list;
          if (codes) {
            codes.forEach(code => options.push({ value: code.id, text: code.label }));
          }
          field.options = options;
        }
      });
    },
    formatDatetime() {
      return (datetime) => !datetime ? null : this.$moment(datetime).format("YYYY-MM-DD HH:mm:ss");
    },
    formatYear() {
      return (datetime) => !datetime ? null : this.$moment(datetime).format("YYYY");
    },
    formatCode(field) {
      return (value) => {
        const code = field.options.find(opt => opt.value === value);
        return code ? code.text : null;
      }
    },
    formatString() {
      return val => val;
    },
    formatNumeric() {
      return val => this.formatNumber(val)
    },
    setLoading(bool) {
      this.$emit('update:loading', bool);
    },
    onRowClicked(item, index) {
      this.$emit('rowClicked', item, index);
    },
    onSortSwitched(name) {
      // Switch sort order
      this.switchSort(name);

      // Generate sorting options
      let sortOptions = this.tableFields.filter(field => field.sortable && field.sortOrder)
          .map(field => ({ name: field.name, order: field.sortOrder }));

      // Sort
      this.dynamicSort(this.tableItems, sortOptions);
    },
    switchSort(name) {
      let field = this.tableFields.find(field => field.name === name);
      if (!field || !field.sortable) return;

      // Switch sorting order
      if (!field.sortOrder) {
        field.sortOrder = 'ASC';
      } else if (field.sortOrder === 'ASC') {
        field.sortOrder = 'DESC';
      } else if (field.sortOrder === 'DESC') {
        field.sortOrder = null;
      }
    },
    dynamicSort(items, sortingOptions) {
      items.sort((a, b) => {
        for (const option of sortingOptions) {
          const fieldName = option.name;
          const sortOrder = option.order === 'ASC' ? 1 : -1;

          const valueA = a[fieldName];
          const valueB = b[fieldName];

          if (typeof valueA === 'number' && typeof valueB === 'number') {
            if (valueA < valueB) return -1 * sortOrder;
            if (valueA > valueB) return 1 * sortOrder;
          } else if (typeof valueA === 'string' && typeof valueB === 'string') {
            const comparison = valueA.localeCompare(valueB);
            if (comparison !== 0) return comparison * sortOrder;
          } else {
            // Handle other data types or mixed types if needed.
            return 0; // No specific order for other data types
          }
        }
        return 0; // Objects are considered equal
      });
    },
    onFilterUpdated(filters) {
      let tableItems = structuredClone(this.items);

      for (const filter of filters) {
        let name = filter.name;
        let type = filter.type;

        tableItems = tableItems.filter(item => {
          if (type === 'String') {
            return filter.value ? item[name].toLowerCase().includes(filter.value.toLowerCase()) : true
          } else if (type === 'Number') {
            return item[name] === filter.value;
          } else if (type === 'Date') {
            let time = new Date(item[name]).getTime();
            let start = new Date(filter.duration[0]).getTime();
            let end = new Date(filter.duration[1]).getTime();
            return start <= time <= end;
          } else {
            return true;
          }
        });
      }
      this.tableItems = tableItems;
    },
    slotname(field) {
      return `cell(${field.name})`;
    }
  },
}
</script>